import React, { useState, useEffect } from 'react';
import { useAuth } from '../../services/contexts/authContext';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';
import { STATIC_USER_ROLES } from '../../utils/enums';
import PulseWaveLogo from '../../assets/svg-pulse-wave-logo';
import PrivacyPolicyModal from './privacyPolicyModal';
import TermsOfServiceModal from './termsOfServiceModal';
import { URLS } from '../../router-x/routes';
import { getHomePageRedirectUrl } from '../../utils/functions';

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { login, user, userDetails, loading: authLoading } = useAuth();

  useEffect(() => {
    if (user && userDetails && !authLoading) {
      navigate(getHomePageRedirectUrl(userDetails.role));
    }
  }, [user, userDetails, authLoading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const resp = await login(email, password);
      if (resp) {
        const user = resp.data.user;
        if (user.role === STATIC_USER_ROLES.USER) {
          navigate(URLS.CUSTOMER.HOME);
        }
        if ([
          STATIC_USER_ROLES.ADMIN,
          STATIC_USER_ROLES.MANAGER,
          STATIC_USER_ROLES.EMPLOYEE,
          STATIC_USER_ROLES.STAFF
        ].includes(user.role)) {
          navigate(URLS.ADMIN.HOME);
        }
        if (user.role === STATIC_USER_ROLES.SUPPORT_AGENT) {
          navigate(URLS.SUPPORT_AGENT.HOME);
        }
        if (user.role === STATIC_USER_ROLES.TEAM_MEMBER) {
          navigate(URLS.TEAM_MEMBER.HOME);
        }
        if (user.role === STATIC_USER_ROLES.RECEPTIONIST) {
          navigate(URLS.RECEPTIONIST.HOME);
        }
      }
      else {
        setError('Failed to login. Try again.');
      }
    } catch (error) {
      setError(
        error.code === 'auth/user-not-found' ? 'User not found' :
          error.code === 'auth/wrong-password' ? 'Incorrect password' :
            'Failed to login. Please check your credentials.'
      );
      console.error('Login error:', error);
    }

    setLoading(false);
  };

  const openPrivacyModal = () => {
    setIsPrivacyModalOpen(true);
  };

  const closePrivacyModal = () => {
    setIsPrivacyModalOpen(false);
  };

  const openTermsModal = () => {
    setIsTermsModalOpen(true);
  };

  const closeTermsModal = () => {
    setIsTermsModalOpen(false);
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden p-6"
      style={{ background: 'radial-gradient(120% 90% at 80% 8%, #C9651B 0%, #9A480E 44%, #4A2E1A 100%)' }}
    >
      {/* Decorative rings */}
      <div className="pointer-events-none absolute -left-[6%] -top-[16%] h-[560px] w-[560px] rounded-full border border-white/[0.09]" />
      <div className="pointer-events-none absolute -bottom-[18%] right-[4%] h-[480px] w-[480px] rounded-full border border-white/[0.06]" />

      <div className="relative w-full max-w-[430px] animate-popIn rounded-[22px] bg-white p-9 shadow-[0_30px_70px_-30px_rgba(40,22,8,0.65)] sm:p-10">
        <div className="flex flex-col items-center">
          <PulseWaveLogo width={98} height={50} />
          <div className="mt-4 pl-[6px] font-display text-[27px] font-bold tracking-[0.34em] text-ink">
            PULSE
          </div>
          <div className="mt-1.5 font-display text-[13.5px] font-semibold text-brand">
            Workspace Management System
          </div>
        </div>

        {error && (
          <div className="mt-6 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="mt-6 space-y-4">
          <div>
            <label htmlFor="email" className="mb-1.5 block text-[12.5px] font-semibold text-[#3B2A1E]">
              Email Address <span className="text-brand">*</span>
            </label>
            <div className="flex h-12 items-center gap-2.5 rounded-xl border-[1.5px] border-cream-200 bg-cream px-3.5 transition-colors focus-within:border-brand">
              <Mail size={18} className="shrink-0 text-[#B7AC9E]" />
              <input
                id="email"
                name="email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent text-sm text-[#2B2420] outline-none placeholder:text-[#B7AC9E]"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-[12.5px] font-semibold text-[#3B2A1E]">
              Password <span className="text-brand">*</span>
            </label>
            <div className="flex h-12 items-center gap-2.5 rounded-xl border-[1.5px] border-cream-200 bg-cream px-3.5 transition-colors focus-within:border-brand">
              <Lock size={18} className="shrink-0 text-[#B7AC9E]" />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent text-sm text-[#2B2420] outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="shrink-0 text-[#B7AC9E] transition-colors hover:text-[#6B675F]"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <Link
              to={URLS.ADMIN.AUTH_FORGOT_PASSWORD}
              className="text-[13px] font-semibold text-brand hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="flex h-[50px] w-full items-center justify-center gap-2 rounded-xl bg-brand font-display text-[15px] font-semibold text-white shadow-[0_10px_20px_-10px_rgba(190,83,16,0.7)] transition-all hover:-translate-y-0.5 hover:bg-brand-dark hover:shadow-[0_16px_28px_-10px_rgba(190,83,16,0.85)] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Signing in...</span>
              </>
            ) : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 border-t border-cream-200 pt-4">
          <p className="text-center text-xs leading-relaxed text-[#8C8175]">
            We respect your privacy. Your information is kept secure and used only to improve your experience.
            By continuing, you accept our{' '}
            <button type="button" onClick={openPrivacyModal} className="font-semibold text-brand hover:underline">Privacy Policy</button>
            {' '}and{' '}
            <button type="button" onClick={openTermsModal} className="font-semibold text-brand hover:underline">Terms of Service</button>.
          </p>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={isPrivacyModalOpen}
        onClose={closePrivacyModal}
      />

      {/* Terms of Service Modal */}
      <TermsOfServiceModal
        isOpen={isTermsModalOpen}
        onClose={closeTermsModal}
      />
    </div>
  );
};

export default Auth;
